import { ldb } from '@codesuma/baseline'
import { Delta, applied, deltaFrom, withPut, withRemove } from './delta'
import type { Factory, ICollection } from './index'
import { local } from './local'

// Changes that have not reached the real database, and the engine a session
// drops to when that database will not open at all.
//
// Two things end up here, and they are the same thing. A session that fell
// back has every change waiting; a healthy session that hit a failed write has
// one. Both are rows the store does not yet hold, both are kept as a delta —
// see services/storage/delta.ts — and both are put right the same way: replay
// them by id, then forget them.
//
// A fallback session must not simply write into the plain localStorage arrays
// the way services/storage/local.ts does. Those arrays are the pre-migration
// snapshot and the rollback, so an edit written there has nowhere to go: the
// import that would carry it across has already run once and is held shut by
// its own flag, and the next healthy launch reads the real database, where the
// edit is not.
//
// What none of this can do is make a fallback session's view current. The
// database that failed to open is the only place newer rows are, so the user
// sees the snapshot with their own session laid on top. Saying so is the app's
// job — see components/storage-notice.

const KEY = (name: string) => `pending-${name}`
// Which collections have something waiting. Kept rather than guessed, so a
// collection added later is replayed without this file being told about it.
const NAMES = 'pending-collections'

const read = (name: string): Delta => deltaFrom(ldb.get(KEY(name)))

export const pendingCollections = (): string[] => {
    const saved = ldb.get(NAMES)
    return Array.isArray(saved) ? saved.filter(name => typeof name === 'string') : []
}

function write(name: string, delta: Delta) {
    ldb.set(KEY(name), delta)
    const names = pendingCollections()
    if (!names.includes(name)) ldb.set(NAMES, [...names, name])
}

// A change the store would not take, kept where the next write on this
// collection and the next launch will both find it.
export const rememberPut = (name: string, row: { id: string }) =>
    write(name, withPut(read(name), row))

export const rememberRemove = (name: string, id: string) =>
    write(name, withRemove(read(name), id))

// Puts one collection's waiting changes into the engine and forgets them.
// Every step is by id and can be repeated, so a throw part way leaves the
// whole delta in place for the next attempt rather than half of it.
export async function flushPending(name: string, engine: Factory) {
    const delta = read(name)
    const ids = Object.keys(delta)
    if (!ids.length) return
    const store = engine<{ id: string }>(name)
    for (const id of ids) {
        const change = delta[id]
        if (change.op === 'put') await store.put(change.row)
        else await store.remove(id)
    }
    // Only what was replayed comes off. A write that failed while this ran has
    // put an entry of its own in the meantime, under a new id or as a newer
    // change under one of these, and that one is still owed.
    const rest = read(name)
    for (const id of ids) {
        if (JSON.stringify(rest[id]) === JSON.stringify(delta[id])) delete rest[id]
    }
    if (Object.keys(rest).length) {
        ldb.set(KEY(name), rest)
        return
    }
    ldb.remove(KEY(name))
    ldb.set(NAMES, pendingCollections().filter(other => other !== name))
}

// Called at boot after the import, so a delta always lands on top of the
// snapshot it was made against rather than under it.
export async function flushAll(engine: Factory) {
    for (const name of pendingCollections()) await flushPending(name, engine)
}

export const fallback = <T extends { id: string }>(name: string): ICollection<T> => ({
    async load() {
        return applied(await local<T>(name).load(), read(name))
    },
    async put(row) {
        write(name, withPut(read(name), row))
    },
    async remove(id) {
        write(name, withRemove(read(name), id))
    },
})
